import React, { createContext, useContext, useState, useEffect } from 'react';
import { dashboardAPI } from '../services/api';
import { setAuthToken, AUTH_EXPIRED_EVENT, PASSWORD_CHANGE_EVENT } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Mock users database - removed as we're using backend authentication only

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);


  // The API layer fires this when a call comes back 401. Dropping the user is
  // all it takes — App renders the login screen when there is no user.
  useEffect(() => {
    const onExpired = () => setUser(null);
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  // The server said a new password is needed first — show that screen.
  useEffect(() => {
    const onChangeRequired = () => setUser(prev => {
      if (!prev || prev.must_change_password) return prev;
      const next = { ...prev, must_change_password: true };
      try { localStorage.setItem('salesTrackerUser', JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
    window.addEventListener(PASSWORD_CHANGE_EVENT, onChangeRequired);
    return () => window.removeEventListener(PASSWORD_CHANGE_EVENT, onChangeRequired);
  }, []);

  useEffect(() => {
    // Check if user is logged in from localStorage
    const savedUser = localStorage.getItem('salesTrackerUser');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    
    try {
      // Try backend API first
      const result = await dashboardAPI.login(email, password);
      
      console.log('📥 Login API response:', result);
      
      if (result.success && result.user) {
        const userData = {
          id: result.user.id,
          username: result.user.username,
          name: result.user.name,
          email: result.user.email,
          role: result.user.role || 'bde',
          email_sender_slot: result.user.email_sender_slot != null ? result.user.email_sender_slot : null,
          ...(result.mustChangePassword ? { must_change_password: true } : {})
        };
        
        console.log('✅ Setting user data:', userData);
        // The API rejects every call without this token.
        setAuthToken(result.token || null);
        setUser(userData);
        localStorage.setItem('salesTrackerUser', JSON.stringify(userData));
        setLoading(false);
        return { success: true, user: userData };
      } else {
        // If backend fails, return error (no mock fallback for email-based login)
        console.log('❌ Login failed:', result.error);
        setLoading(false);
        return { success: false, error: result.error || 'Invalid email or password' };
      }
    } catch (error) {
      console.error('❌ Login exception:', error);
      console.error('Error details:', error.response?.data || error.message);
      // Return error (no mock fallback for email-based login)
      setLoading(false);
      return { success: false, error: error.response?.data?.error || 'Login failed. Please check your credentials or try again later.' };
    }
  };

  // The new password is saved: swap in the normal session the server returned.
  const completePasswordChange = (result) => {
    if (!result || !result.token || !result.user) return;
    setAuthToken(result.token);
    const userData = { ...result.user };
    delete userData.must_change_password;
    setUser(userData);
    localStorage.setItem('salesTrackerUser', JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    setAuthToken(null);
    localStorage.removeItem('salesTrackerUser');
    // Clear any AI-assistant chats for any user id stored this session.
    try {
      const keys = Object.keys(sessionStorage);
      keys.forEach(k => { if (k.startsWith('ai-chat:')) sessionStorage.removeItem(k); });
    } catch { /* sessionStorage unavailable — ignore */ }
  };

  /**
   * Log in from Suite (SSO): set session from validated Suite token payload.
   * Same storage as normal login so "is user logged in?" and logout work as usual.
   * @param {object} payload - From Suite validate API (e.g. { sub, email, name, role })
   * @param {string} loginHint - login_hint query param (user email from Suite)
   */
  /**
   * Suite SSO. The Suite token is now exchanged for a Sales Hub token that the
   * BACKEND issues after verifying the token with Suite itself — the browser
   * saying "this token is fine" is not evidence the API can act on.
   */
  const loginFromSuiteToken = async (suiteToken, loginHint) => {
    try {
      const res = await dashboardAPI.createSuiteSession({ suite_token: suiteToken, email: loginHint });
      if (res?.success && res.token) {
        setAuthToken(res.token);
        setUser(res.user);
        localStorage.setItem('salesTrackerUser', JSON.stringify(res.user));
        return { success: true, user: res.user };
      }
      return { success: false, error: res?.error || 'Suite sign-in failed.' };
    } catch (e) {
      return { success: false, error: e.response?.data?.error || e.message };
    }
  };

  const loginFromSuite = (payload, loginHint) => {
    const email = payload?.email || loginHint || payload?.sub || '';
    const userData = {
      id: payload?.sub ?? payload?.id ?? null,
      username: payload?.username || (email ? email.split('@')[0] : 'user'),
      name: payload?.name || email || 'User',
      email: email,
      role: payload?.role || 'bde',
      email_sender_slot: payload?.email_sender_slot != null ? payload.email_sender_slot : null
    };
    setUser(userData);
    localStorage.setItem('salesTrackerUser', JSON.stringify(userData));
    return { success: true, user: userData };
  };

  const isAdmin = () => {
    return user && user.role === 'admin';
  };

  const isBDE = () => {
    return user && user.role === 'bde';
  };

  const [allUsers, setAllUsers] = useState([]);
  const [bdeUsers, setBdeUsers] = useState([]);
  const bdeUsersFetchedRef = React.useRef(false);

  // Fetch users from API
  const fetchUsers = async () => {
    try {
      const response = await dashboardAPI.getAllUsers();
      if (response.success && response.data) {
        setAllUsers(response.data);
        return response.data;
      }
      // If API fails, return empty array (don't use mock data)
      setAllUsers([]);
      return [];
    } catch (error) {
      console.error('Error fetching users:', error);
      setAllUsers([]);
      return [];
    }
  };

  const getAllUsers = () => {
    // Always return cached users (they should be fetched and set by fetchUsers)
    return allUsers.length > 0 ? allUsers : [];
  };

  const addUser = async (userData) => {
    try {
      const response = await dashboardAPI.createUser(userData);
      if (response.success) {
        // Refresh users list
        await fetchUsers();
        return { success: true, user: response.data };
      }
      return { success: false, error: response.error || 'Failed to create user' };
    } catch (error) {
      console.error('Error creating user:', error);
      return { success: false, error: error.response?.data?.error || error.message || 'Failed to create user' };
    }
  };

  const updateUser = async (userId, userData) => {
    try {
      const response = await dashboardAPI.updateUser(userId, userData);
      if (response.success) {
        // Refresh users list
        await fetchUsers();
        return { success: true, user: response.data };
      }
      return { success: false, error: response.error || 'Failed to update user' };
    } catch (error) {
      console.error('Error updating user:', error);
      return { success: false, error: error.response?.data?.error || error.message || 'Failed to update user' };
    }
  };

  const deleteUser = async (userId) => {
    try {
      const response = await dashboardAPI.deleteUser(userId);
      if (response.success) {
        // Refresh users list
        await fetchUsers();
        return { success: true };
      }
      return { success: false, error: response.error || 'Failed to delete user' };
    } catch (error) {
      console.error('Error deleting user:', error);
      return { success: false, error: error.response?.data?.error || error.message || 'Failed to delete user' };
    }
  };

  const fetchBDEUsers = async () => {
    if (bdeUsersFetchedRef.current && bdeUsers.length > 0) return bdeUsers;
    try {
      const response = await dashboardAPI.getBDEUsers();
      if (response && response.data) {
        setBdeUsers(response.data);
        bdeUsersFetchedRef.current = true;
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching BDE users:', error);
    }
    return [];
  };

  // Fetch users on mount
  useEffect(() => {
    fetchUsers();
    fetchBDEUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    user,
    loading,
    login,
    loginFromSuite,
    loginFromSuiteToken,
    logout,
    completePasswordChange,
    isAdmin,
    isBDE,
    getAllUsers,
    addUser,
    updateUser,
    deleteUser,
    refreshUsers: fetchUsers,
    bdeUsers,
    refreshBDEUsers: fetchBDEUsers
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
